import {
  isHlsFile,
  PREFERRED_HLS_VERSION,
  useBrowserSupportsHls,
} from '../../../util/video-util.ts'
import { CSSObject, useTheme } from '@emotion/react'
import ReactPlayer from 'react-player'
import styled from '@emotion/styled'
import { useEffect, useRef, useState } from 'react'
import { PlayIcon, VolumeOffIcon, VolumeOnIcon } from './VideoIcons.tsx'
import { fetchSubtitles, Subtitles } from './Subtitles.tsx'
import { getFirstFrameUrl } from '../../../util/path.ts'

interface VideoPlayerProps {
  videoUrl: string
  playing: boolean
  controls: 'none' | 'browserDefault' | 'custom'
  muted: boolean
  loop: boolean
  alt: string
  subtitleLang: string
  fullscreen?: boolean
  className?: string
  onBufferEnd?: () => void
  onEnded?: () => void
  onMuteChange?: (muted: boolean) => void
}

export const VideoPlayer = (props: VideoPlayerProps) => {
  const browserSupportsHls = useBrowserSupportsHls()
  const theme = useTheme()

  const [playing, setPlaying] = useState(props.playing)
  const [muted, setMuted] = useState(props.muted)
  const [progressValue, setProgressValue] = useState<number>(0)
  const [duration, setDuration] = useState<number>(0)
  const [subtitles, setSubtitles] = useState<string>()

  const [showClickTarget, setShowClickTarget] = useState<boolean>(true)
  const [showPlayButton, setShowPlayButton] = useState(false)
  const [showCustomControls, setShowCustomControls] = useState(false)

  const playerRef = useRef<ReactPlayer>(null)
  const intervalId = useRef<number>()

  // When the playing prop changes, override our internal playing state
  useEffect(() => {
    setPlaying(props.playing)
    setShowCustomControls(props.playing)
  }, [props.playing])

  // When the mute prop changes, override our internal mute state
  useEffect(() => {
    setMuted(props.muted)
  }, [props.muted])

  useEffect(() => {
    // on browserDefault we want show the click target only to grab first click
    if (props.controls === 'browserDefault') {
      if (playing) {
        setShowClickTarget(false)
      }
    }

    // always, since we use it to toggle play/pause
    if (props.controls === 'custom') {
      setShowClickTarget(true)
    }

    if (props.controls === 'none') {
      setShowClickTarget(false)
    }
  }, [playing, props.controls])

  // Fetch subtitles when videoUrl or subtitleLang changes
  useEffect(() => {
    if (
      props.controls === 'custom' &&
      !subtitles &&
      props.videoUrl &&
      props.subtitleLang
    ) {
      fetchSubtitles(props.videoUrl, props.subtitleLang)
        .then((subtitles) => {
          if (subtitles && subtitles.length > 0) {
            setSubtitles(subtitles)
          }
        })
        .catch((error: unknown) => {
          console.error(
            `Failed to fetch subtitles. Video: '${props.videoUrl}', lang '${error}'`
          )
        })
    }
  }, [props.videoUrl, props.subtitleLang, props.controls, subtitles])

  // Update the progressValue periodically when video is playing
  useEffect(() => {
    if (props.controls === 'custom' && playing) {
      intervalId.current = window.setInterval(() => {
        if (playerRef.current) {
          const currentTime = playerRef.current.getCurrentTime()
          const duration = playerRef.current.getDuration()

          if (
            !Number.isNaN(currentTime) &&
            !Number.isNaN(duration) &&
            duration > 0
          ) {
            const played = (currentTime / duration) * 100
            setProgressValue(played)
          }
        }
      }, 30)
    } else if (intervalId.current) {
      clearInterval(intervalId.current)
      intervalId.current = undefined
    }

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current)
      }
    }
  }, [props.controls, playing])

  const clickedProgressBar = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()

    if (playerRef.current && duration > 0) {
      const progressBarRect = event.currentTarget.getBoundingClientRect()
      const clickPositionX = event.clientX - progressBarRect.left
      const progressBarWidth = progressBarRect.width
      const clickRatio = clickPositionX / progressBarWidth

      const newTime = clickRatio * duration
      playerRef.current.seekTo(newTime, 'seconds')
      setProgressValue(clickRatio * 100)
    }
  }

  const togglePlayPause = () => {
    const newPlayingState = !playing
    setPlaying(newPlayingState)
    setShowPlayButton(!newPlayingState)
  }

  const updateMuted = (muted: boolean) => {
    setMuted(muted)
    props.onMuteChange?.(muted)
  }

  const toggleMuted = () => {
    updateMuted(!muted)
  }

  const posterUrl = getFirstFrameUrl(props.videoUrl)

  return (
    <VideoContainer fullscreen={props.fullscreen}>
      {showClickTarget && <PlayPauseClickTarget onClick={togglePlayPause} playing={playing} />}

      {props.controls !== 'none' && (
        <FadeInOutDiv visible={props.controls === 'browserDefault' ? showClickTarget : showPlayButton }>
          <CenteredControlsPanel>
            <PlayIcon width="100px" height="100px" icon={theme.videoPlayIcon} />
          </CenteredControlsPanel>
        </FadeInOutDiv>
      )}

      {props.controls === 'custom' && (
        <FadeInOutDiv visible={showCustomControls}>
          <MuteContainer muted={muted} onClick={toggleMuted}>
            {muted && <VolumeOffIcon width="30px" height="30px" icon={''} />}
            {!muted && <VolumeOnIcon width="30px" height="30px" icon={''} />}
          </MuteContainer>

          <BottomControlsPanel>
            {subtitles && duration > 0 && (
              <SubtitleContainer>
                <Subtitles
                  subtitles={subtitles}
                  progress={progressValue}
                  duration={duration}
                />
              </SubtitleContainer>
            )}

            <ProgressBarContainer onClick={clickedProgressBar}>
              <ProgressBar value={progressValue} max={100} />
              <ProgressKnob style={{ left: `calc(${progressValue}%)` }} />
            </ProgressBarContainer>
          </BottomControlsPanel>
        </FadeInOutDiv>
      )}

      <StyledReactPlayer
        ref={playerRef}
        url={props.videoUrl}
        className={props.className ?? ''}
        // Controls should initially be hidden on browserDefault, but then always stay
        controls={props.controls === 'browserDefault' && !showClickTarget}
        playing={playing}
        muted={muted}
        loop={props.loop}
        alt={props.alt}
        width="100%"
        height="100%"
        playsinline={true}
        onPause={() => setPlaying(false)}
        onBufferEnd={props.onBufferEnd}
        onPlay={() => setPlaying(true)}
        onDuration={(duration) => setDuration(duration)}
        onEnded={() => {
          setPlaying(false)
          props.onEnded?.()
        }}
        onError={(error) => {
          if (error?.name === 'NotAllowedError') {
            console.log(
              'Error playing video unmuted. User probably didnt interact with the page yet. Will mute the video and try again...'
            )
            updateMuted(true)
          }
        }}
        config={{
          file: {
            forceHLS: isHlsFile(props.videoUrl) && !browserSupportsHls,
            hlsVersion: PREFERRED_HLS_VERSION,
            attributes: {
              poster: posterUrl,
            }
          },
        }}
      />
    </VideoContainer>
  )
}

const VideoContainer = styled.div<{ fullscreen?: boolean }>`
  position: relative;
  ${(props): CSSObject =>
    props.fullscreen
      ? {
        position: 'relative',
        width: '100%',
        height: '100vh',
        video: {
          objectFit: 'cover',
        },
      }
      : {}}
`

const StyledReactPlayer = styled(ReactPlayer)`
  display: flex;
`

const FadeInOutDiv = styled.div<{ visible: boolean }>`
  ${({ visible }) => (visible ? 'opacity: 1;' : 'opacity: 0;')}
  transition: opacity 0.3s ease-in-out;
`

const BottomControlsPanel = styled.div`
  position: absolute;
  z-index: 2;

  bottom: 0;
  width: 100%;
  padding: 0 30px env(safe-area-inset-bottom, 0px) 30px;
  pointer-events: none;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`

const CenteredControlsPanel = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;

  display: flex;
  flex: 0 0 auto;
`

const PlayPauseClickTarget = styled.div<{ playing: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  cursor: ${({ playing }) => (playing ? 'auto' : 'pointer')};
  pointer-events: auto;

  z-index: 1;
`

const MuteContainer = styled.div<{ muted: boolean }>`
  position: absolute;
  z-index: 2;
  pointer-events: auto;

  top: 20px;
  right: 20px;
  width: 50px;
  cursor: pointer;
  height: 50px;

  border-radius: 20%;

  display: flex;
  justify-content: center;
  align-items: center;

  ${({ muted, theme }) => {
    if (muted) {
      return `
        background-color: white;

        svg {
          path {
            color: black;
          }
        }

        ${theme.videoControlsPrimaryCss}
      `
    } else {
      return `
        background-color: rgba(0, 0, 0, 0.25);

        svg {
          path {
            color: white;
          }
        }

        ${theme.videoControlsSecondaryCss}
      `
    }
  }}
`

const SubtitleContainer = styled.div`
  width: 100%;
  bottom: 20px;
  margin-bottom: 20px;
`

const ProgressBarContainer = styled.div`
  position: relative;
  width: 100%;
  height: 30px;
  margin-bottom: 20px;
  cursor: pointer;
  pointer-events: auto;

  display: flex;
  flex-direction: column;
  justify-content: center;
`

const ProgressBar = styled.progress`
  position: absolute;
  width: 100%;
  height: 6px;
  appearance: none;

  background-color: rgba(0, 0, 0, 0.25);
  border: none;
  border-radius: 0.15em;
  ${({ theme }) => theme.videoControlsSecondaryCss}

  &::-webkit-progress-bar {
    background-color: rgba(0, 0, 0, 0.25);
    border-radius: 0.15em;
    ${({ theme }) => theme.videoControlsSecondaryCss}
  }
  &::-webkit-progress-value {
    border-radius: 0.15em;
    background-color: white;
    ${({ theme }) => theme.videoControlsPrimaryCss}
  }
  &::-moz-progress-bar {
    border-radius: 0.15em;
    background-color: white;
    ${({ theme }) => theme.videoControlsPrimaryCss}
  }
`

const ProgressKnob = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  background-color: white;
  border: none;
  border-radius: 50%;
  transform: translateX(-50%);

  ${({ theme }) => theme.videoControlsPrimaryCss}
`
